import React, { useEffect, useRef, useState } from "react";
import { Radio, ChevronLeft, ChevronRight, Heart, Users, Share2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { LiveViewer, shareLiveEvent, countdown } from "./modules/LiveEvents";

const API = process.env.REACT_APP_BACKEND_URL;

// Homepage "Live Now" strip — auto-scrolling carousel with manual controls; public lives viewable by anyone
export default function LiveNow({ currentUser, page = "landing" }) {
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [watching, setWatching] = useState(null);
  const [paused, setPaused] = useState(false);
  // Masters choose which pages show live previews (Master Control → Site Content)
  const [allowed, setAllowed] = useState(true);
  useEffect(() => {
    fetch(`${API}/api/site/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => setAllowed(!s || (s.live_preview_pages || []).includes(page)))
      .catch(() => {});
  }, [page]);
  const trackRef = useRef(null);

  useEffect(() => {
    const load = () => {
      fetch(`${API}/api/live/active`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setEvents(Array.isArray(data) ? data.filter((e) => e.is_public) : []))
        .catch((err) => console.debug("live strip failed", err));
      fetch(`${API}/api/live/upcoming`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setUpcoming(Array.isArray(data) ? data.filter((e) => e.is_public) : []))
        .catch((err) => console.debug("upcoming strip failed", err));
    };
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  // Auto-scroll loop (pauses on hover / manual interaction)
  useEffect(() => {
    if (events.length + upcoming.length < 2) return undefined;
    const t = setInterval(() => {
      const el = trackRef.current;
      if (!el || paused) return;
      const card = el.querySelector("[data-livecard]");
      const step = card ? card.offsetWidth + 16 : 320;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 10) el.scrollTo({ left: 0, behavior: "smooth" });
      else el.scrollBy({ left: step, behavior: "smooth" });
    }, 4000);
    return () => clearInterval(t);
  }, [events.length, upcoming.length, paused]);

  if (!allowed || (!events.length && !upcoming.length && !watching)) return null;

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-livecard]");
    el.scrollBy({ left: (card ? card.offsetWidth + 16 : 320) * dir, behavior: "smooth" });
  };

  return (
    <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full" data-testid="live-now-section">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
            <Radio className="w-5 h-5 text-rose-400" /> {events.length ? "Live Now" : "Coming Up Live"}
          </h2>
          <p className="text-sm text-[#6B7280] dark:text-slate-300 mt-1">Watch events happening across NestTrack daycares — chime in, heart, or join live.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => scroll(-1)} data-testid="live-now-prev" className="w-9 h-9 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] hover:bg-[#FFF9F2] transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => scroll(1)} data-testid="live-now-next" className="w-9 h-9 rounded-full bg-white border border-[#FCE6E9] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151] hover:bg-[#FFF9F2] transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={trackRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="flex gap-4 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        data-testid="live-now-track"
      >
        {events.map((ev) => (
          <button
            key={ev.id}
            data-livecard
            data-testid={`live-now-card-${ev.id}`}
            onClick={() => setWatching(ev.id)}
            className="min-w-[300px] sm:min-w-[340px] bg-white rounded-3xl border border-[#FCE6E9] shadow-[0_10px_30px_-12px_rgba(248,200,220,0.5)] p-5 text-left hover:-translate-y-1 transition-transform duration-200 dark:bg-[#273244] dark:border-[#374457]"
          >
            <div className="relative h-36 -mx-5 -mt-5 mb-3 overflow-hidden rounded-t-3xl bg-gradient-to-br from-[#FCE6E9] via-[#FFF9F2] to-[#E6F2FA]">
              {ev.poster ? (
                <img src={ev.poster} alt={ev.title} className="w-full h-full object-cover" data-testid={`live-poster-${ev.id}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Radio className="w-10 h-10 text-rose-300" />
                </div>
              )}
              <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-rose-500 text-white text-[10px] font-extrabold uppercase rounded-full animate-pulse shadow">
                <Radio className="w-3 h-3" /> Live Now
              </span>
              <span className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 bg-white/90 text-[#374151] text-[10px] font-bold rounded-full shadow-sm">
                <Heart className="w-3 h-3" /> {ev.hearts}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  shareLiveEvent(ev);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    shareLiveEvent(ev);
                  }
                }}
                data-testid={`live-share-${ev.id}`}
                title="Share this live event — full preview card with host, title and description"
                className="absolute bottom-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#374151] shadow cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5" />
              </span>
            </div>
            <p className="text-sm font-black font-heading text-[#374151] dark:text-slate-100 truncate">{ev.title}</p>
            <p className="text-[11px] text-[#9CA3AF] font-medium mt-1 truncate">
              {ev.host_name} · {ev.facilityName || "NestTrack"}
            </p>
            <p className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-rose-500 text-white text-[11px] font-extrabold rounded-full shadow-sm">
              <Users className="w-3 h-3" /> Watch Live
            </p>
          </button>
        ))}
        {upcoming.map((ev) => (
          <button
            key={ev.id}
            data-livecard
            data-testid={`live-upcoming-card-${ev.id}`}
            onClick={() => toast.info(`Starts ${new Date(ev.scheduled_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} — we'll notify you the moment it starts.`)}
            className="min-w-[300px] sm:min-w-[340px] bg-white rounded-3xl border border-[#B3E0F2] shadow-[0_10px_30px_-12px_rgba(179,224,242,0.6)] p-5 text-left hover:-translate-y-1 transition-transform duration-200 dark:bg-[#273244] dark:border-[#374457]"
          >
            <div className="relative h-36 -mx-5 -mt-5 mb-3 overflow-hidden rounded-t-3xl bg-gradient-to-br from-[#E6F2FA] via-[#FFF9F2] to-[#FCE6E9]">
              {ev.poster ? (
                <img src={ev.poster} alt={ev.title} className="w-full h-full object-cover" data-testid={`live-upcoming-poster-${ev.id}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <CalendarClock className="w-10 h-10 text-[#5CB3DC]" />
                </div>
              )}
              <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 bg-[#5CB3DC] text-white text-[10px] font-extrabold uppercase rounded-full shadow">
                <CalendarClock className="w-3 h-3" /> {countdown(ev.scheduled_at)}
              </span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  shareLiveEvent(ev);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    shareLiveEvent(ev);
                  }
                }}
                data-testid={`live-share-${ev.id}`}
                title="Share this upcoming event — preview card carries the watch link"
                className="absolute bottom-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center text-[#6B7280] hover:text-[#374151] shadow cursor-pointer"
              >
                <Share2 className="w-3.5 h-3.5" />
              </span>
            </div>
            <p className="text-sm font-black font-heading text-[#374151] dark:text-slate-100 truncate">{ev.title}</p>
            <p className="text-[11px] text-[#9CA3AF] font-medium mt-1 truncate">
              {ev.host_name} · {ev.facilityName || "NestTrack"}
            </p>
            <p className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 bg-[#5CB3DC] text-white text-[11px] font-extrabold rounded-full shadow-sm" data-testid={`live-upcoming-when-${ev.id}`}>
              <CalendarClock className="w-3 h-3" /> {new Date(ev.scheduled_at).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          </button>
        ))}
      </div>
      {watching && <LiveViewer eventId={watching} currentUser={currentUser} onClose={() => setWatching(null)} />}
    </section>
  );
}
